import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Box,
  TextField,
  Chip,
  LinearProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import i18n from 'i18n-js';

import { updateOnboarding } from '../../reducers/User/actions';
import { AnalyticsProvider } from '../../providers';
import {
  ONBOARDING_QUESTIONS,
  TOTAL_STEPS,
  ONBOARDING_INITIAL_STATE,
} from '../../constants/onboarding';
import PricingDialog from '../PricingDialog';
import DiscountOffer from '../DiscountOffer';
import {
  Container,
  ProgressContainer,
  ProgressDots,
  Dot,
  ContentContainer,
  QuestionContainer,
  OptionsContainer,
  OptionButton,
  MultiSelectContainer,
  ActionsContainer,
} from './styles';

export default function OnboardingModal({ open, onClose }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [multiSelectValue, setMultiSelectValue] = useState([]);
  const [textValue, setTextValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showDiscountPricing, setShowDiscountPricing] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const currentQuestion = ONBOARDING_QUESTIONS[currentStep];

  useEffect(() => {
    const onboardingData = user.data?.onboarding || ONBOARDING_INITIAL_STATE;
    setCurrentStep(onboardingData.step || 0);
    setAnswers(onboardingData);

    // Initialize multi-select and text values
    if (currentQuestion?.type === 'multi-select') {
      setMultiSelectValue(onboardingData[currentQuestion.field] || []);
    } else if (currentQuestion?.type === 'text') {
      setTextValue(onboardingData[currentQuestion.field] || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.data?.onboarding, currentStep]);

  const handleSingleSelect = async (value) => {
    const newAnswers = {
      ...answers,
      [currentQuestion.field]: value,
      step: currentStep + 1,
    };

    setAnswers(newAnswers);
    setIsSubmitting(true);

    try {
      await dispatch(updateOnboarding(newAnswers));

      // Track step completion
      AnalyticsProvider.event('ONBOARDING_STEP_COMPLETED', {
        step: currentStep,
        stepName: currentQuestion.field,
        selectedValue: value,
        totalSteps: TOTAL_STEPS,
      });

      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error updating onboarding:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMultiSelectToggle = (value) => {
    const newValue = multiSelectValue.includes(value)
      ? multiSelectValue.filter((v) => v !== value)
      : [...multiSelectValue, value];
    setMultiSelectValue(newValue);
  };

  const handleMultiSelectNext = async () => {
    const newAnswers = {
      ...answers,
      [currentQuestion.field]: multiSelectValue,
      step: currentStep + 1,
    };

    setAnswers(newAnswers);
    setIsSubmitting(true);

    try {
      await dispatch(updateOnboarding(newAnswers));

      // Track step completion
      AnalyticsProvider.event('ONBOARDING_STEP_COMPLETED', {
        step: currentStep,
        stepName: currentQuestion.field,
        selectedValue: multiSelectValue,
        totalSteps: TOTAL_STEPS,
      });

      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Error updating onboarding:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextNext = async () => {
    const newAnswers = {
      ...answers,
      [currentQuestion.field]: textValue,
      step: currentStep + 1,
      completed: true,
      completedAt: new Date().toISOString(),
    };

    setAnswers(newAnswers);
    setIsSubmitting(true);
    setIsCompleting(true);

    try {
      await dispatch(updateOnboarding(newAnswers));

      // Track step completion
      AnalyticsProvider.event('ONBOARDING_STEP_COMPLETED', {
        step: currentStep,
        stepName: currentQuestion.field,
        selectedValue: textValue,
        totalSteps: TOTAL_STEPS,
      });

      AnalyticsProvider.event('ONBOARDING_COMPLETED', {
        totalSteps: TOTAL_STEPS,
      });

      setIsCompleting(false);
      setShowSuccess(true);
      
      // Only show pricing if user is not already premium
      if (!user.data.premium) {
        setShowPricing(true);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error updating onboarding:', error);
      setIsCompleting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    AnalyticsProvider.event('ONBOARDING_SKIPPED', {
      skippedAtStep: currentStep,
    });

    onClose();
  };

  const renderProgressDots = () => {
    return (
      <ProgressContainer>
        <ProgressDots>
          {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
            <Dot
              key={index}
              active={index === currentStep}
              completed={index < currentStep}
            />
          ))}
        </ProgressDots>
      </ProgressContainer>
    );
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    return (
      <QuestionContainer>
        <Typography variant="h4" gutterBottom>
          {i18n.t(currentQuestion.titleKey)}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {i18n.t(currentQuestion.subtitleKey)}
        </Typography>
      </QuestionContainer>
    );
  };

  const renderOptions = () => {
    if (!currentQuestion) return null;

    if (currentQuestion.type === 'single-select') {
      return (
        <OptionsContainer>
          {currentQuestion.options.map((option) => (
            <OptionButton
              key={option.value}
              variant="outlined"
              onClick={() => handleSingleSelect(option.value)}
              disabled={isSubmitting}
            >
              {i18n.t(option.labelKey)}
            </OptionButton>
          ))}
        </OptionsContainer>
      );
    }

    if (currentQuestion.type === 'multi-select') {
      return (
        <Box>
          <MultiSelectContainer>
            {currentQuestion.options.map((option) => (
              <Chip
                key={option.value}
                label={i18n.t(option.labelKey)}
                onClick={() => handleMultiSelectToggle(option.value)}
                variant={
                  multiSelectValue.includes(option.value)
                    ? 'filled'
                    : 'outlined'
                }
                color={
                  multiSelectValue.includes(option.value)
                    ? 'primary'
                    : 'default'
                }
                clickable
              />
            ))}
          </MultiSelectContainer>
          <Button
            variant="contained"
            onClick={handleMultiSelectNext}
            disabled={isSubmitting || multiSelectValue.length === 0}
            fullWidth
          >
            {i18n.t('onboarding.next')}
          </Button>
        </Box>
      );
    }

    if (currentQuestion.type === 'text') {
      return (
        <Box sx={{ width: '100%', maxWidth: 500 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            placeholder={i18n.t(currentQuestion.placeholderKey)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleTextNext}
            disabled={isSubmitting}
            fullWidth
          >
            {i18n.t('onboarding.finish')}
          </Button>
        </Box>
      );
    }

    return null;
  };

  const onCloseDialog = () => {
    if (!isSubmitting) {
      handleSkip();
    }
  };

  const handlePricingClose = () => {
    setShowPricing(false);
    // Show discount pricing when closing regular pricing during onboarding
    setShowDiscountPricing(true);
  };

  const handleDiscountPricingClose = () => {
    setShowDiscountPricing(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !showPricing && !showDiscountPricing}
        onClose={onCloseDialog}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown={isSubmitting}
        sx={{ '& .MuiDialog-paper': { position: 'relative' } }}
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {isSubmitting && <LinearProgress />}

          <IconButton
            aria-label="close"
            onClick={handleSkip}
            disabled={isSubmitting}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1000,
              color: (theme) => theme.palette.grey[500],
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Container>
            {renderProgressDots()}

            <ContentContainer>
              {isCompleting ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {showSuccess ? i18n.t('onboarding.completed') : i18n.t('onboarding.completing')}
                  </Typography>
                </Box>
              ) : (
                <>
                  {renderQuestion()}
                  {renderOptions()}
                </>
              )}
            </ContentContainer>

            <ActionsContainer>
              <Button
                variant="text"
                color="secondary"
                onClick={handleSkip}
                disabled={isSubmitting}
              >
                {i18n.t('onboarding.skip')}
              </Button>
            </ActionsContainer>
          </Container>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {i18n.t('onboarding.completed')}
        </Alert>
      </Snackbar>

      <PricingDialog
        open={showPricing}
        onClose={handlePricingClose}
      />

      <DiscountOffer
        open={showDiscountPricing}
        onClose={handleDiscountPricingClose}
      />
    </>
  );
}
